import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Send, Rocket, ArrowLeft } from 'lucide-react';
import { contactFieldKey } from './contactFieldKey';
import { buildRecipients, buildVariables, normalizePhone } from './recipientMapping';
import { whatsappService } from 'api/services/whatsapp.service';

// CSV campaign builder: pick template -> detect {{n}} variables -> upload CSV ->
// map columns -> preview + test-send -> launch with per-recipient variables.
//
// The mapping itself lives in ./recipientMapping, shared with "add a whole file"
// on a standing campaign, so one file cannot map two ways depending on the screen
// it was dropped on.
const WhatsAppCampaignBuilder: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<any[]>([]);
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  /**
   * Which column is the person's NAME, and which are worth keeping about them.
   *
   * Separate from varMap, which maps the TEMPLATE's {{1}}/{{2}} placeholders.
   * Those are argument slots — their meaning lives in the template, and the same
   * slot means something else in the next campaign — so they are never stored on
   * a contact. This is how a positional file still says who somebody is.
   */
  const [nameCol, setNameCol] = useState<number>(-1);
  const [fieldCols, setFieldCols] = useState<number[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [phoneCol, setPhoneCol] = useState<number>(-1);
  const [varMap, setVarMap] = useState<Record<string, number>>({}); // var token -> column index
  const [sendRate, setSendRate] = useState(60);
  const [readingFile, setReadingFile] = useState(false);
  const [fileNote, setFileNote] = useState<string | null>(null);
  const [standing, setStanding] = useState(false);
  const [scheduleAt, setScheduleAt] = useState(''); // datetime-local; empty = send now
  const [suppressDays, setSuppressDays] = useState(7); // skip numbers messaged in last N days (0 = off)
  const [wabaHealth, setWabaHealth] = useState<any>(null);
  const [testNumber, setTestNumber] = useState('');
  const [testMsg, setTestMsg] = useState<string | null>(null);
  const [launching, setLaunching] = useState(false);
  const [launchMsg, setLaunchMsg] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      // Use the dedicated approved endpoint — it returns ALL approved templates.
      // getTemplates({status:'APPROVED'}) is paginated and defaults to size 20,
      // so a business with >20 approved templates only saw the first 20 here.
      const t = await whatsappService.getApprovedTemplates();
      const list = (t.success && t.data ? (t.data as any) : []) as any[];
      setTemplates(Array.isArray(list) ? list : []);
      const h = await whatsappService.getWabaHealth();
      if (h.success && h.data) setWabaHealth(h.data);
    })();
  }, []);

  const template = useMemo(() => templates.find((t) => t.id === templateId), [templates, templateId]);

  // Unique {{n}} tokens in body order.
  const variables = useMemo(() => {
    const body: string = template?.bodyText || '';
    const found = (body.match(/\{\{\s*(\w+)\s*\}\}/g) || []).map((m) => m.replace(/[{}]/g, '').trim());
    return Array.from(new Set(found));
  }, [template]);

  /**
   * Read a CSV or XLSX through the server.
   *
   * Both go the same way on purpose. The browser cannot open XLSX at all — and most
   * lists arrive as XLSX — and letting CSV take a second, browser-side path would mean
   * the same file could map differently depending on how it was saved.
   */
  const onFile = async (file?: File) => {
    if (!file) return;
    setFileNote(null);
    setReadingFile(true);
    const r = await whatsappService.parseRecipientFile(file);
    setReadingFile(false);
    if (!r.success || !r.data) { setFileNote(r.error || 'Could not read that file'); return; }

    const { headers: hs, rows: rs, totalRows, truncated } = r.data;
    if (!hs?.length) { setFileNote('That file has no header row'); return; }

    // The mapping UI indexes columns positionally, so header-keyed rows come back to
    // arrays in header order.
    setHeaders(hs);
    setRows(rs.map((row) => hs.map((h) => row[h] ?? '')));
    const guess = hs.findIndex((h) => /phone|mobile|number|msisdn|contact/i.test(h));
    setPhoneCol(guess >= 0 ? guess : 0);
    // Never silent. A shortened list is how somebody messages 50,000 of 200,000
    // people and believes the campaign went to everyone.
    setFileNote(truncated
      ? `Read ${rs.length.toLocaleString()} of ${totalRows.toLocaleString()} rows — the rest were not loaded.`
      : null);
  };

  /** Which column is which, as mapped above. */
  const mapping = { headers, phoneCol, slots: variables, varMap, nameCol, fieldCols };

  const rowVariables = (row: string[]) => buildVariables(row, mapping);

  const renderPreview = (row?: string[]): string => {
    let body: string = template?.bodyText || '';
    if (row) variables.forEach((tok) => { const col = varMap[tok]; body = body.split(`{{${tok}}}`).join(col != null ? (row[col] ?? `{{${tok}}}`) : `{{${tok}}}`); });
    return body;
  };

  const recipients = useMemo(() => buildRecipients(rows, { headers, phoneCol, slots: variables, varMap, nameCol, fieldCols }),
    [rows, phoneCol, varMap, variables, nameCol, fieldCols, headers]); // the mapping derives from these

  const allVarsMapped = variables.every((t) => varMap[t] != null && varMap[t] >= 0);
  const ready = !!name && !!templateId && phoneCol >= 0 && recipients.length > 0 && allVarsMapped;

  const doTestSend = async () => {
    setTestMsg(null);
    if (!testNumber || !templateId) { setTestMsg('Enter a test number first.'); return; }
    const vars = rows[0] ? rowVariables(rows[0]) : {};
    const res = await whatsappService.testSendCampaign({ templateId, toNumber: normalizePhone(testNumber), variables: vars });
    setTestMsg(res.success ? 'Test sent ✓ (check WhatsApp)' : (res.error || 'Test send failed'));
  };

  const doLaunch = async () => {
    setLaunching(true);
    setLaunchMsg(null);
    const scheduledMillis = scheduleAt ? new Date(scheduleAt).getTime() : undefined;
    const res = await whatsappService.createCampaign({
      name,
      templateId,
      targetAudience: JSON.stringify({ type: 'CSV' }),
      sendRate,
      standing,
      recipients,
      suppressRecentDays: suppressDays > 0 ? suppressDays : undefined,
      ...(scheduledMillis ? { scheduledAt: scheduledMillis } : {}),
    } as any);
    if (res.success && res.data) {
      const cid = (res.data as any).id;
      if (scheduledMillis) {
        // Created as SCHEDULED; the server-side scheduler launches it at send time.
        setLaunchMsg(`Scheduled ✓ for ${new Date(scheduledMillis).toLocaleString()} — ${recipients.length} recipients`);
        setTimeout(() => navigate('/whatsapp/campaigns'), 1800);
      } else {
        const started = await whatsappService.startCampaign(cid);
        setLaunchMsg(started.success ? `Launched ✓ — ${recipients.length} recipients sending` : (started.error || 'Created but failed to start'));
        if (started.success) setTimeout(() => navigate('/whatsapp/campaigns'), 1500);
      }
    } else {
      setLaunchMsg(res.error || 'Failed to create campaign');
    }
    setLaunching(false);
  };

  const ColSelect = ({ value, onChange }: { value: number; onChange: (n: number) => void }) => (
    <select value={value} onChange={(e) => onChange(Number(e.target.value))}
      className="px-2 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded text-sm">
      <option value={-1}>— select column —</option>
      {headers.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
    </select>
  );

  return (
    <div className="p-6 max-w-3xl space-y-6">
      <button onClick={() => navigate('/whatsapp/campaigns')} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Campaigns
      </button>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">New CSV Campaign</h1>

      {/* WABA deliverability / cooldown guard */}
      {wabaHealth && (
        <div className={`rounded-lg border p-3 text-sm ${
          wabaHealth.level === 'HIGH' ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800 text-red-800 dark:text-red-200'
          : wabaHealth.level === 'CAUTION' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-200'
          : 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-800 text-green-800 dark:text-green-200'}`}>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-semibold">WABA {wabaHealth.level}</span>
            <span>Frequency-cap rate (24h): <b>{wabaHealth.recentCapRatePct}%</b> over {wabaHealth.settledSample} sends</span>
            {wabaHealth.qualityRating && <span>Quality: <b>{wabaHealth.qualityRating}</b></span>}
            {wabaHealth.messagingTier && <span>Tier: <b>{wabaHealth.messagingTier}</b></span>}
          </div>
          <p className="mt-1 opacity-90">{wabaHealth.recommendation}</p>
        </div>
      )}

      {/* 1. Basics */}
      <section className="space-y-3 bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <h2 className="font-semibold text-gray-800 dark:text-gray-200">1. Campaign & template</h2>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Campaign name"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg" />
        <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg">
          <option value="">Select an approved template…</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name || t.templateName}</option>)}
        </select>
        {template && (
          <p className="text-xs text-gray-500">
            Detected {variables.length} variable{variables.length === 1 ? '' : 's'}: {variables.map((v) => `{{${v}}}`).join(', ') || 'none'}
          </p>
        )}
      </section>

      {/* 2. CSV upload */}
      <section className="space-y-3 bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <h2 className="font-semibold text-gray-800 dark:text-gray-200">2. Upload recipients (CSV or Excel)</h2>
        <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 text-sm">
          <Upload className="w-4 h-4" /> {readingFile ? 'Reading…' : 'Choose file'}
          <input
            type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            disabled={readingFile}
            onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }}
          />
        </label>
        {headers.length > 0 && <span className="text-sm text-gray-600 dark:text-gray-300 ml-2">{rows.length} rows, {headers.length} columns</span>}
              {fileNote && <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">{fileNote}</p>}

        <label className="flex items-start gap-3 pt-3 mt-3 border-t border-gray-100 dark:border-gray-700 cursor-pointer">
          <input type="checkbox" checked={standing} onChange={(e) => setStanding(e.target.checked)} className="mt-1" />
          <span className="text-sm">
            <span className="font-medium text-gray-800 dark:text-gray-100">Keep this campaign open</span>
            <span className="block text-gray-500 dark:text-gray-400 mt-0.5">
              It never completes — you can keep adding numbers afterwards, one at a time, from a
              form or from an agent after a call. Leave this off for a one-off send to the list above.
            </span>
          </span>
        </label>
      </section>

      {/* 3. Mapping */}
      {headers.length > 0 && (
        <section className="space-y-3 bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <h2 className="font-semibold text-gray-800 dark:text-gray-200">3. Map columns</h2>
          <div className="flex items-center gap-3 text-sm">
            <span className="w-40 text-gray-700 dark:text-gray-300">Phone number →</span>
            <ColSelect value={phoneCol} onChange={setPhoneCol} />
          </div>
          {variables.map((tok) => (
            <div key={tok} className="flex items-center gap-3 text-sm">
              <span className="w-40 text-gray-700 dark:text-gray-300">Variable {`{{${tok}}}`} →</span>
              <ColSelect value={varMap[tok] ?? -1} onChange={(n) => setVarMap((m) => ({ ...m, [tok]: n }))} />
            </div>
          ))}
          <div className="pt-3 mt-1 border-t border-gray-200 dark:border-gray-700 space-y-3">
            <div>
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                What to remember about these people <span className="font-normal text-gray-400">(optional)</span>
              </h3>
              <p className="text-xs text-gray-500 mt-1 max-w-3xl">
                Everyone in the file becomes a contact when the campaign is created. The variables
                above fill the template — <b>{'{{1}}'}, {'{{2}}'}</b> and so on are argument slots, and
                what they mean changes with the template, so they are not kept on the person. Say
                here which columns are about <i>them</i>, and the agent that calls them later will
                know it.
              </p>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="w-40 text-gray-700 dark:text-gray-300">Their name →</span>
              <ColSelect value={nameCol} onChange={setNameCol} />
            </div>
            <div className="text-sm">
              <span className="block text-gray-700 dark:text-gray-300 mb-1">Also keep</span>
              <div className="flex flex-wrap gap-2">
                {headers.map((h, i) => {
                  const key = contactFieldKey(h, i);
                  const on = fieldCols.includes(i);
                  // A column that cannot be a field is shown disabled WITH the
                  // reason: silently omitting it reads as a rendering bug.
                  const why = !key
                    ? (/^[0-9]+$/.test((h || '').trim()) ? 'a template slot, not a fact'
                       : (h || '').trim() ? 'reserved by the template sender' : 'unnamed column')
                    : null;
                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={!key || i === nameCol || i === phoneCol}
                      title={why || (i === nameCol ? 'kept as their name' : i === phoneCol ? 'kept as their number' : `stored as ${key}`)}
                      onClick={() => setFieldCols((c) => c.includes(i) ? c.filter((x) => x !== i) : [...c, i])}
                      className={`px-2 py-1 rounded border text-xs ${
                        on ? 'bg-green-50 border-green-300 text-green-800 dark:bg-green-900/20 dark:text-green-200'
                           : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                      } disabled:opacity-40 disabled:cursor-not-allowed`}
                    >
                      {h || `Column ${i + 1}`}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500">Valid recipients after mapping: <b>{recipients.length}</b></p>
        </section>
      )}

      {/* 4. Preview + test */}
      {template && rows.length > 0 && (
        <section className="space-y-3 bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <h2 className="font-semibold text-gray-800 dark:text-gray-200">4. Preview & test</h2>
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm whitespace-pre-line text-gray-800 dark:text-gray-100">
            {renderPreview(rows[0])}
          </div>
          <p className="text-xs text-gray-400">Preview uses row 1 of your CSV.</p>
          <div className="flex items-center gap-2">
            <input value={testNumber} onChange={(e) => setTestNumber(e.target.value)} placeholder="Test number e.g. +9199…"
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm" />
            <button onClick={doTestSend} className="px-3 py-2 bg-gray-700 text-white rounded-lg text-sm flex items-center gap-1 hover:bg-gray-800">
              <Send className="w-4 h-4" /> Send test
            </button>
            {testMsg && <span className="text-sm text-gray-600 dark:text-gray-300">{testMsg}</span>}
          </div>
        </section>
      )}

      {/* 5. Launch */}
      <section className="space-y-3 bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <h2 className="font-semibold text-gray-800 dark:text-gray-200">5. Launch</h2>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-gray-700 dark:text-gray-300">Send rate (msgs/min)</span>
          <input type="number" min={1} max={300} value={sendRate} onChange={(e) => setSendRate(Number(e.target.value))}
            className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded text-sm" />
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-gray-700 dark:text-gray-300">Skip numbers messaged in last</span>
          <input type="number" min={0} max={90} value={suppressDays} onChange={(e) => setSuppressDays(Number(e.target.value))}
            className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded text-sm" />
          <span className="text-gray-500 dark:text-gray-400">days (0 = off) — protects against frequency caps</span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-gray-700 dark:text-gray-300">Schedule (optional)</span>
          <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)}
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded text-sm" />
          {scheduleAt && <button onClick={() => setScheduleAt('')} className="text-xs text-gray-400 hover:text-gray-600">clear (send now)</button>}
        </div>
        <button onClick={doLaunch} disabled={!ready || launching}
          className="tl-btn tl-btn-primary disabled:opacity-50">
          <Rocket className="w-4 h-4" /> {launching ? 'Submitting…' : scheduleAt ? `Schedule ${recipients.length} recipients` : `Launch to ${recipients.length} recipients`}
        </button>
        {!ready && <p className="text-xs text-gray-400">Fill name, template, upload CSV, map the phone column and every variable.</p>}
        {launchMsg && <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{launchMsg}</p>}
      </section>
    </div>
  );
};

export default WhatsAppCampaignBuilder;
